import React, { useCallback, useEffect, useState } from 'react';
import { Message } from '@kvdbil/components';
import { VerifyBankID } from '~/App/shared/components/Auth/VerifyBankID';
import { useTranslation } from '~/Locale';
import { useDispatch, useSelector } from 'react-redux';
import { verifyPhoneNumberSelector } from '~/App/shared/selectors/verifyPhoneNumberSelector';
import {
  resetPhoneVerification,
  sendVerificationCode
} from '~/App/shared/actions/verifyPhoneNumber';
import EnterCode from '~/App/shared/components/VerifyPhoneNumber/components/EnterCode';
import StatusPageLoading from '~/App/shared/components/StatusPageLoading';
import OrganisationMemberDetailsForm, {
  FormData
} from './OrganisationMemberDetailsForm';
import { useMemberDataUpdate } from '../../hooks/useMemberDataUpdate';

interface Props {
  isBankIdVerificationPending: boolean;
  setIsBankIdVerificationPending: (isPending: boolean) => void;
  isPhoneVerificationPending: boolean;
  setIsPhoneVerificationPending: (isPending: boolean) => void;
}

const BiddingOrgPersonBankIdAndPhoneVerification = ({
  isBankIdVerificationPending,
  setIsBankIdVerificationPending,
  isPhoneVerificationPending,
  setIsPhoneVerificationPending
}: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { error, generalError, isSendingCode } = useSelector(
    verifyPhoneNumberSelector
  );
  const [organisationDetailsFormData, setOrganisationDetailsFormData] =
    useState<FormData | null>(null);
  const {
    saveAccountDetailsCallback,
    isSavingAccountDetails,
    isSavingAccountDetailsError
  } = useMemberDataUpdate();
  const isError = error || generalError || isSavingAccountDetailsError;

  useEffect(() => {
    return () => dispatch(resetPhoneVerification());
  }, [dispatch]);

  const onSuccessBankIdVerificationCallback = useCallback(() => {
    setTimeout(() => {
      organisationDetailsFormData &&
        void saveAccountDetailsCallback(organisationDetailsFormData);
      dispatch(sendVerificationCode());
      setIsPhoneVerificationPending(true);
      setIsBankIdVerificationPending(false);
    }, 2000);
  }, [
    setIsBankIdVerificationPending,
    setIsPhoneVerificationPending,
    dispatch,
    saveAccountDetailsCallback,
    organisationDetailsFormData
  ]);

  if (isBankIdVerificationPending) {
    return (
      <VerifyBankID
        onClose={() => setIsBankIdVerificationPending(false)}
        onSuccess={onSuccessBankIdVerificationCallback}
      />
    );
  }

  if (isPhoneVerificationPending) {
    return (
      <>
        {(isSendingCode || isSavingAccountDetails) && <StatusPageLoading />}
        {isError && (
          <Message type="error" fullWidth>
            {error || t('Something went wrong, please try again later.')}
          </Message>
        )}
        {!isSendingCode && !isSavingAccountDetails && !isError && (
          <EnterCode handleClose={() => setIsPhoneVerificationPending(false)} />
        )}
      </>
    );
  }

  const handleVerifyWithBankIdClick = () => {
    setIsBankIdVerificationPending(true);
  };

  return (
    <OrganisationMemberDetailsForm
      organisationDetailsFormData={organisationDetailsFormData}
      setOrganisationDetailsFormData={setOrganisationDetailsFormData}
      handleSubmitButtonClick={handleVerifyWithBankIdClick}
      accountVerificationStatus="VERIFY_ORG_PERSON_BANKID_AND_PHONE"
    />
  );
};

export default BiddingOrgPersonBankIdAndPhoneVerification;
